/**
 * Числовая прямая: SVG, нарисованный из строки-спецификации.
 *
 * **Почему рисуем кодом, а не берём картинку.** Картинка про математику
 * обязана быть верной до подписи под каждой засечкой, а порождённая
 * изображением — верна на глаз: выколотая точка оказывается закрашенной,
 * −3 подписано как 3, штриховка уходит не в ту сторону. Проверять такую
 * картинку дороже, чем нарисовать её из данных, и ошибку в ней ученик примет
 * за правило. Здесь картинка — функция от той же строки, что стоит в тексте:
 * разойтись с ней она не может.
 *
 * Заодно это решает три вещи даром: тема оформления (цвета из токенов, а не
 * запечены в пиксели), экран телефона (вектор, а не растр) и вес страницы.
 *
 * **Язык спецификации — три формы, больше не нужно.**
 *
 *     x > 3            луч вправо, точка выколота
 *     x <= -2          луч влево, точка закрашена
 *     -1 < x <= 4      промежуток, левый конец выколот, правый закрашен
 *     points: -5, -2, 2, 5   просто отмеченные числа
 *
 * Знаки пишутся ASCII (`<=`, `>=`) — их набирают с клавиатуры, и в diff они не
 * ломаются. На экране подписи рисуются настоящими «≤» и «≥».
 */

type Bound = { at: number; closed: boolean };

type Spec =
  | { kind: "ray"; dir: "left" | "right"; bound: Bound }
  | { kind: "segment"; from: Bound; to: Bound }
  | { kind: "points"; at: number[] };

const NUM = String.raw`-?\d+(?:[.,]\d+)?`;
const RAY = new RegExp(`^x\\s*(<=|>=|<|>)\\s*(${NUM})$`);
const SEGMENT = new RegExp(`^(${NUM})\\s*(<=|<)\\s*x\\s*(<=|<)\\s*(${NUM})$`);

function num(raw: string): number {
  return Number(raw.replace(",", "."));
}

/** `null` — строка не разобрана. Экран тогда покажет её текстом, а не молчит. */
export function parseNumberLine(source: string): Spec | null {
  const s = source.trim();
  if (s.startsWith("points:")) {
    const at = s
      .slice("points:".length)
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p !== "")
      .map(num);
    return at.length > 0 && at.every(Number.isFinite) ? { kind: "points", at } : null;
  }
  const ray = RAY.exec(s);
  if (ray !== null) {
    const op = ray[1]!;
    const at = num(ray[2]!);
    if (!Number.isFinite(at)) return null;
    return {
      kind: "ray",
      dir: op.startsWith(">") ? "right" : "left",
      bound: { at, closed: op.length === 2 },
    };
  }
  const seg = SEGMENT.exec(s);
  if (seg !== null) {
    const from = num(seg[1]!);
    const to = num(seg[4]!);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) return null;
    return {
      kind: "segment",
      from: { at: from, closed: seg[2] === "<=" },
      to: { at: to, closed: seg[3] === "<=" },
    };
  }
  return null;
}

/** Числа, которые картинка обязана назвать: границы или отмеченные точки. */
function marksOf(spec: Spec): number[] {
  return spec.kind === "ray"
    ? [spec.bound.at]
    : spec.kind === "segment"
      ? [spec.from.at, spec.to.at]
      : spec.at;
}

/**
 * Подписанные засечки.
 *
 * **Каждое отмеченное число подписано — это не удобство, а верность картинки.**
 * 14 сентября 2026 «points: -5, -2, 2, 5» нарисовалось с шагом два: подписи
 * встали на −7, −5, −3, −1, 1, 3, 5, 7, а точки −2 и 2 повисли между ними. На
 * такой картинке ученик читает не те числа, о которых идёт речь в тексте.
 *
 * Отсюда две ветки. Отмеченные числа сами становятся засечками: сетка им не
 * нужна, речь идёт ровно о них. У луча и промежутка сетка целых остаётся — по
 * ней видно, где проходит граница, — но границы в неё добавлены принудительно.
 *
 * Шаг сетки растёт вместе с размахом: промежуток от −1 до 40 иначе нарисовал бы
 * сорок три подписи в одну кашу.
 */
export function ticksFor(spec: Spec): number[] {
  const marks = marksOf(spec);
  if (spec.kind === "points") return [...marks].sort((a, b) => a - b);
  const lo = Math.floor(Math.min(...marks)) - 2;
  const hi = Math.ceil(Math.max(...marks)) + 2;
  const step = Math.max(1, Math.ceil((hi - lo) / 8));
  const out = new Set<number>(marks);
  for (let v = lo; v <= hi; v += step) out.add(v);
  return [...out].sort((a, b) => a - b);
}

const W = 320;
const H = 58;
const AXIS = 34;
const PAD = 18;

/** Подпись числа: минус настоящий, дробная часть через запятую. */
function label(value: number): string {
  return String(value).replace("-", "−").replace(".", ",");
}

export function NumberLine({ source }: { source: string }) {
  const spec = parseNumberLine(source);
  if (spec === null) {
    // Неразобранная строка показывается как есть. Молчать нельзя: пустое место
    // на странице объяснения неотличимо от «так и задумано».
    return <div className="faint">не удалось нарисовать: {source}</div>;
  }
  const ticks = ticksFor(spec);
  // У отмеченных чисел крайние точки стоят по краям картинки, и поля им нужны,
  // иначе точка садится на стрелку оси. У луча и промежутка поля уже заложены
  // сеткой: она шире границ на два шага.
  const pad = spec.kind === "points" ? 1 : 0;
  const lo = ticks[0]! - pad;
  const hi = ticks[ticks.length - 1]! + pad;
  const at = (v: number): number => PAD + ((v - lo) / (hi - lo)) * (W - 2 * PAD);

  const dot = (b: Bound, key: string) => (
    <circle
      key={key}
      cx={at(b.at)}
      cy={AXIS}
      r={5}
      className={b.closed ? "nline__dot nline__dot--closed" : "nline__dot"}
    />
  );

  return (
    <svg className="nline" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={source}>
      {/* Ось со стрелкой вправо: направление роста чисел показывается один раз. */}
      <line className="nline__axis" x1={4} y1={AXIS} x2={W - 4} y2={AXIS} />
      <polygon className="nline__arrow" points={`${W - 4},${AXIS} ${W - 12},${AXIS - 4} ${W - 12},${AXIS + 4}`} />

      {/* Закраска решения. Рисуется ПОД точками и засечками, иначе съедает их. */}
      {spec.kind === "ray" ? (
        <line
          className="nline__fill"
          x1={spec.dir === "right" ? at(spec.bound.at) : 6}
          y1={AXIS}
          x2={spec.dir === "right" ? W - 10 : at(spec.bound.at)}
          y2={AXIS}
        />
      ) : null}
      {spec.kind === "segment" ? (
        <line className="nline__fill" x1={at(spec.from.at)} y1={AXIS} x2={at(spec.to.at)} y2={AXIS} />
      ) : null}

      {ticks.map((v) => (
        <g key={v}>
          <line className="nline__tick" x1={at(v)} y1={AXIS - 5} x2={at(v)} y2={AXIS + 5} />
          <text className="nline__label num" x={at(v)} y={AXIS + 20} textAnchor="middle">
            {label(v)}
          </text>
        </g>
      ))}

      {spec.kind === "ray" ? dot(spec.bound, "b") : null}
      {spec.kind === "segment" ? [dot(spec.from, "f"), dot(spec.to, "t")] : null}
      {spec.kind === "points"
        ? spec.at.map((v) => (
            <circle key={v} cx={at(v)} cy={AXIS} r={5} className="nline__dot nline__dot--closed" />
          ))
        : null}
    </svg>
  );
}
